import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import stickerMakerApi from '../../api/stickerMakerApi';
import stickerMakerUrls from "../../configs/sticker_maker/stickerMakerUrls";
import { StickerPackInterface } from "../../store/sticker_maker/stickerPacksSlice";
import { userEntity, usersEntity } from "./usersActions";
import { UserInterface } from "../../store/sticker_maker/usersSlice";
import {message} from "antd";

const PREFIX = 'sticker_maker';

const processStrategy = (value: any) => {
  return {
    ...value,
    url: stickerMakerUrls.stickerPackPath(value.id),
    image_url: stickerMakerUrls.remoteUrl(value.image_url)
  };
}

export const stickerPacksEntity = new schema.Entity(
  'sticker_packs',
  {
    user: usersEntity
  },
  {
    processStrategy
  }
);

export const stickerPackEntity = new schema.Entity(
  'sticker_pack',
  {
    user: userEntity
  },
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      user: { [key: string]: UserInterface };
      sticker_pack: StickerPackInterface
    }
    >(response.data.sticker_pack, stickerPackEntity);
  return normalized.entities;
}

const normalizeCollection = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      users: { [key: string]: UserInterface };
      sticker_packs: { [key: string]: StickerPackInterface };
    }
    >(response.data.sticker_packs, [stickerPacksEntity]);

  return {
    users: normalized.entities.users || {},
    sticker_packs: normalized.entities?.sticker_packs || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

export const fetchStickerPacks = createAsyncThunk(
  `${PREFIX}_stickerPacks/fetchStickerPacks`,
  async ({ params, target, collection }: { params: object; target: string; collection?: string }) => {
    let url = `/onesky/${target}`;
    url = collection ? `${url}/${collection}` : url;

    const response = await stickerMakerApi.get(url, { params });
    return normalizeCollection(response);
  }
);

export const fetchReviews = createAsyncThunk(
  `${PREFIX}_stickerPacks/fetchStickerPacks`,
  async ({ params, target, collection }: { params: object; target: string; collection?: string }) => {
    const response = await stickerMakerApi.get('onesky/sticker_packs/reviews', { params });
    return normalizeCollection(response);
  }
);

export const fetchUserStickerPacks = createAsyncThunk(
  `${PREFIX}_stickerPacks/fetchStickerPacks`,
  async ({ userId, params = {} }: { userId: number | string; params?: object; }) => {
    const response = await stickerMakerApi.get(`onesky/users/${userId}/sticker_packs`, { params });
    return normalizeCollection(response);
  }
);

export const fetchStickerPack = createAsyncThunk(
  `${PREFIX}_stickerPacks/fetchStickerPack`,
  async (id: number | string) => {
    const response = await stickerMakerApi.get(`/onesky/sticker_packs/${id}`);
    return normalizeInstance(response);
  }
);

export const createStickerPack = createAsyncThunk(
  `${PREFIX}_stickerPacks/createStickerPack`,
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.post(`/onesky/sticker_packs`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateStickerPack = createAsyncThunk(
  `${PREFIX}_stickerPacks/updateStickerPack`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.patch(`/onesky/sticker_packs/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyStickerPack = createAsyncThunk(
  `${PREFIX}_stickerPacks/destroyStickerPack`,
  async (id: number) => {
    const response = await stickerMakerApi.delete(`/onesky/sticker_packs/${id}`);
    return response.data;
  }
);

export const blockStickerPack = createAsyncThunk(
  `${PREFIX}_stickerPacks/updateStickerPack`,
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.patch(`/onesky/sticker_packs/${id}/block`);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const unBlockStickerPack = createAsyncThunk(
  `${PREFIX}_stickerPacks/updateStickerPack`,
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.patch(`/onesky/sticker_packs/${id}/unblock`);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const publishStickerPack = createAsyncThunk(
  `${PREFIX}_stickerPacks/updateStickerPack`,
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.patch(`/onesky/sticker_packs/${id}/publish`);
      return normalizeInstance(response);
    } catch (error: any) {
      message.error(error.response.data[0]);
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const unPublishStickerPack = createAsyncThunk(
  `${PREFIX}_stickerPacks/updateStickerPack`,
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await stickerMakerApi.patch(`/onesky/sticker_packs/${id}/unpublish`);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);
